import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Wordmark, cx } from '../ui';
import { useAuth } from '../features/auth/AuthProvider';

// `to` is omitted for sections that are not built yet; they render as disabled "Soon" items, never as dead links.
const NAV = [
  { label: 'Campaigns', to: '/dashboard/projects', icon: '▦' },
  { label: 'Panels', icon: '◉' },
  { label: 'Audience', icon: '◎' },
  { label: 'Reports', to: '/dashboard/reports', icon: '▤' },
];
const ACCOUNT = [{ label: 'API settings', to: '/dashboard/ApiSetting', icon: '⚙' }];
// Admins (and superadmins) also manage the people in their organisation; the server enforces it, the link is just hidden.
const USERS_LINK = { label: 'Users', to: '/dashboard/users', icon: '☺' };

const itemClass = (active: boolean) =>
  cx(
    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    active ? 'bg-brand-50 font-semibold text-brand-800' : 'text-muted hover:bg-chip hover:text-ink'
  );

interface NavEntry { label: string; to?: string; icon: string }

function NavItem({ item, onNavigate }: { item: NavEntry; onNavigate: () => void }) {
  if (!item.to) {
    return (
      <li>
        <span className={cx(itemClass(false), 'cursor-not-allowed opacity-60')} aria-disabled="true">
          <span aria-hidden="true">{item.icon}</span>
          {item.label}
          <span className="ml-auto rounded-full bg-chip px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Soon</span>
        </span>
      </li>
    );
  }
  return (
    <li>
      <NavLink to={item.to} onClick={onNavigate} className={({ isActive }) => itemClass(isActive)}>
        <span aria-hidden="true">{item.icon}</span>
        {item.label}
      </NavLink>
    </li>
  );
}

export function AppShell() {
  const { user, logout, isAdmin } = useAuth();
  const accountItems = isAdmin ? [USERS_LINK, ...ACCOUNT] : ACCOUNT;
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const name = [user && user.firstName, user && user.lastName].filter(Boolean).join(' ') || 'Account';
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  const sidebar = (
    <nav aria-label="Main" className="flex h-full flex-col gap-1 p-3">
      <div className="px-3 pb-4 pt-2"><Wordmark /></div>
      <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-muted/70">Workspace</p>
      <ul className="space-y-0.5">{NAV.map((i) => <NavItem key={i.label} item={i} onNavigate={close} />)}</ul>
      <p className="px-3 pb-1 pt-4 text-[11px] font-bold uppercase tracking-wider text-muted/70">Account</p>
      <ul className="space-y-0.5">{accountItems.map((i) => <NavItem key={i.label} item={i} onNavigate={close} />)}</ul>
      <div className="mt-auto border-t border-line pt-3">
        <div className="flex items-center gap-3 px-2 py-1">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-50 text-xs font-bold text-brand-800" aria-hidden="true">{initials}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{name}</p>
            <button type="button" onClick={logout} className="text-xs font-medium text-muted hover:text-ink">Sign out</button>
          </div>
        </div>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-canvas">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-60 flex-none border-r border-line bg-surface md:block">{sidebar}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[1040] md:hidden">
          <div className="absolute inset-0 bg-ink/40" onClick={close} aria-hidden="true" />
          <aside className="absolute inset-y-0 left-0 w-64 bg-surface shadow-pop">{sidebar}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-line bg-surface px-4 md:hidden">
          <button type="button" onClick={() => setOpen(true)} aria-label="Open navigation menu" aria-expanded={open} className="rounded-md p-2 text-ink hover:bg-chip">☰</button>
          <Wordmark />
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
